/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

import React from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

export default function MagiclineErrorHandler({ errorCode }) {
  const { t } = useTranslation();

  /**
     * This array contains all generic categories of magicline errors.
     * Errors with no error message translation are commented out.
     *
     * @type {string[]}
     */
  const MAGICLINE_ERROR_CODES = [
    // 'ENTITY_NOT_FOUND',
    // 'CONFLICT',
    // 'BAD_REQUEST',
    // 'REQUIRED_PARAMETER_MISSING',
    // 'VALIDATION_FAILED',
    // 'FORBIDDEN',
    // 'OPERATION_NOT_ALLOWED_FOR_STUDIO',
    // 'SERVER_ERROR',
    // 'FEATURE_DISABLED',
    // 'STUDIO_NOT_IN_GERMANY',
    // 'VOUCHER_VALIDATION_FAILED_INVALID_CODE',
    // 'VOUCHER_VALIDATION_FAILED_NOT_REDEEMABLE_HERE',
    // 'VOUCHER_VALIDATION_FAILED_NOT_IN_VALIDITY_PERIOD',
    // 'VOUCHER_VALIDATION_FAILED_NOT_ALLOWED_FOR_RATE',
    'TRIALSESSION_ALREADY_BOOKED',
  ];

  /**
     * This function is executed when we have a matching translation for the error code
     *
     * @returns {React.JSX.Element}
     */
  const getSpecificAlertContent = () => (
    <Box sx={{ fontSize: '16px' }}>
      {t(errorCode)}
    </Box>
  );

  /**
     * This function is executed when we have no matching translation for the error code
     *
     * @returns {React.JSX.Element}
     */
  const getBaseAlertContent = () => (
    <Box sx={{ fontSize: '16px' }}>
      {t('baseErrorMessageText')}
    </Box>
  );

  /**
     * Since magicline don't provide translations for their error messages,
     * we need to map the error codes to our own labels
     *
     * @returns {React.JSX.Element}
     */
  const getErrorMessage = () => (MAGICLINE_ERROR_CODES.includes(errorCode) ? getSpecificAlertContent() : getBaseAlertContent());

  return (
    <Alert
      className="form-alert"
      sx={{
        maxWidth: '768px', margin: '24px auto',
      }}
      severity="error"
    >
      <AlertTitle sx={{ fontWeight: 'bold' }}>
        {t('baseErrorMessageTitle')}
      </AlertTitle>
      {getErrorMessage()}
      <Box sx={{ fontSize: '16px', fontWeight: 'bold', marginTop: '7px' }}>
        {t('baseErrorMessageContact')}
      </Box>
    </Alert>
  );
}

MagiclineErrorHandler.propTypes = {
  errorCode: PropTypes.string.isRequired,
};
